/* eslint-disable */
// @ts-nocheck
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Box,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import axios from 'axios';
import { toast } from 'react-toastify';

const BASE_URL = process.env.REACT_APP_ABT_ALL_APIS_BASE_URL;

// Status badge styling (v6 statuses: PENDING, ACTIVE, EXPIRED, CANCELLED)
const STATUS_COLORS = {
  ACTIVE:    'bg-success',
  PENDING:   'bg-warning text-dark',
  EXPIRED:   'bg-secondary',
  CANCELLED: 'bg-danger',
};

export const FmOffersTable = ({
  transactions,
  isLoading,
  convertToLocalTime,
  fareMediaId,
  fareMediaBalance,
  onRefresh,
  hasWriteAccess,
  userPhone,
}) => {
  const intl = useIntl();

  // Purchase modal state
  const [openPurchaseDialog, setOpenPurchaseDialog]  = useState(false);
  const [eligibleProducts, setEligibleProducts]      = useState([]);
  const [selectedProductId, setSelectedProductId]    = useState('');
  const [contractStartDate, setContractStartDate]    = useState('');
  const [saving, setSaving]                          = useState(false);
  const [loadingProducts, setLoadingProducts]        = useState(false);

  // Cancel confirm state
  const [openCancelDialog, setOpenCancelDialog]      = useState(false);
  const [cancelTarget, setCancelTarget]              = useState(null); // { fare_product_instances_id, fare_media_id }
  const [cancelReason, setCancelReason]              = useState('');
  const [cancelling, setCancelling]                  = useState(false);

  const currentLanguageCode = localStorage.getItem('EASY_MOBILITY_LANGUAGE') || 'en-us';

  let userAuthData = '';
  const authData = localStorage.getItem('authData');
  if (authData) {
    const parsed = JSON.parse(authData);
    userAuthData = parsed.userToken;
  }

  const headers = {
    'access-token': userAuthData?.['access-token'],
    uid:            userAuthData?.uid,
    client:         userAuthData?.client,
  };

  // ─── Purchase Flow ───────────────────────────────────────────────────────
  const openPurchaseModal = () => {
    setSelectedProductId('');
    setContractStartDate(new Date().toISOString().split('T')[0]); // default today
    setOpenPurchaseDialog(true);
    setLoadingProducts(true);
    axios
      .get(
        `${BASE_URL}/api/v1/web_portal/fare_media_policy_capping/fare_product_instances/eligible/${fareMediaId}`,
        { headers }
      )
      .then((res) => setEligibleProducts(res.data?.data || []))
      .catch(() => toast.error('Failed to fetch eligible fare products'))
      .finally(() => setLoadingProducts(false));
  };

  const handlePurchase = async () => {
    if (!selectedProductId) return;
    // For SPECIFIC_DATE trigger, a start date is required
    if (selectedProductDetails?.activation_trigger === 'SPECIFIC_DATE' && !contractStartDate) {
      toast.error(intl.formatMessage({ id: 'requiredValidation' }) || 'Contract start date is required');
      return;
    }
    setSaving(true);
    try {
      const payload = { fare_media_id: fareMediaId, discount_bands_id: selectedProductId };
      if (selectedProductDetails?.activation_trigger === 'SPECIFIC_DATE') {
        payload.contract_start_date = contractStartDate;
      }
      await axios.post(
        `${BASE_URL}/api/v1/web_portal/fare_media_policy_capping/fare_product_instances/purchase`,
        payload,
        { headers }
      );
      toast.success(intl.formatMessage({ id: 'SuccessOperationHappend' }) || 'Fare product purchased successfully');
      setOpenPurchaseDialog(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  // ─── Cancel Flow ─────────────────────────────────────────────────────────
  const openCancelModal = (row) => {
    setCancelTarget({ fare_product_instances_id: row.fare_product_instances_id, fare_media_id: fareMediaId });
    setCancelReason('');
    setOpenCancelDialog(true);
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await axios.post(
        `${BASE_URL}/api/v1/web_portal/fare_media_policy_capping/fare_product_instances/cancel`,
        { ...cancelTarget, cancellation_reason: cancelReason },
        { headers }
      );
      toast.success(intl.formatMessage({ id: 'SuccessOperationHappend' }) || 'Fare product instance cancelled.');
      setOpenCancelDialog(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || String(err));
    } finally {
      setCancelling(false);
    }
  };

  // ─── Selected product detail card ────────────────────────────────────────
  const selectedProductDetails = eligibleProducts.find(
    (p) => p.discount_bands_id === selectedProductId
  );

  const isInsufficientBalance = selectedProductDetails && Number(fareMediaBalance || 0) < Number(selectedProductDetails.purchase_price);

  // ─── Loading state ───────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      {/* ── Action bar ──────────────────────────────────────────────────── */}
      {/* v7: Buying a fare product requires the card to be linked to a user
          (added in their app) — gate ONLY the Buy button on that, the table
          of existing fare product instances is shown regardless. */}
      {hasWriteAccess && userPhone && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            style={{ backgroundColor: '#363740', color: '#fff', borderRadius: '6px' }}
            onClick={openPurchaseModal}
          >
            {intl.formatMessage({ id: 'BuyPass' }) || 'Buy Fare Product'}
          </Button>
        </div>
      )}

      {hasWriteAccess && !userPhone && (
        <div style={{ textAlign: 'center', padding: '12px', marginBottom: '8px', color: '#8a6d3b', fontSize: '13px' }}>
          {intl.formatMessage({ id: 'FareProductNoUserHint' }) ||
            "You can't use Buy Fare Product until the user adds this fare media in their app."}
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────────────── */}
      {!transactions || transactions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'gray' }}>
          {intl.formatMessage({ id: 'NoOffersFound' }) || 'No fare product instances found for this fare media.'}
        </div>
      ) : (
        <div className="table-responsive custom-table-responsive" style={{ height: '420px' }}>
          <table className="table custom-table">
            <thead className="table-headers-style">
              <tr>
                <th>{intl.formatMessage({ id: 'PassName' })       || 'Fare Product'}</th>
                <th>{intl.formatMessage({ id: 'PassCode' })       || 'Code'}</th>
                <th>{intl.formatMessage({ id: 'Status' })         || 'Status'}</th>
                <th>{intl.formatMessage({ id: 'ContractDurationDays' }) || 'Duration (Days)'}</th>
                <th>{intl.formatMessage({ id: 'PurchasePrice' })  || 'Price Paid'}</th>
                <th>{intl.formatMessage({ id: 'ConsumedAmount' }) || 'Consumed Amount'}</th>
                <th>{intl.formatMessage({ id: 'ConsumedTrips' }) || 'Consumed Trips'}</th>
                <th>{intl.formatMessage({ id: 'ContractStart' })  || 'Contract Start'}</th>
                <th>{intl.formatMessage({ id: 'ContractEnd' })    || 'Contract End'}</th>
                <th>{intl.formatMessage({ id: 'PurchasedAt' })    || 'Purchased At'}</th>
                <th>{intl.formatMessage({ id: 'ActivatedAt' })    || 'Activated At'}</th>
                <th>{intl.formatMessage({ id: 'CancelledAt' })    || 'Cancelled At'}</th>
                <th>{intl.formatMessage({ id: 'CancellationReason' }) || 'Reason'}</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((row, index) => (
                <tr key={row.fare_product_instances_id || index}>
                  <td>
                    {currentLanguageCode === 'ar-eg'
                      ? row.pass_name || row.pass_en_name
                      : row.pass_en_name || row.pass_name}
                  </td>
                  <td>{row.pass_code}</td>
                  <td>
                    <span className={`badge ${STATUS_COLORS[row.status] || 'bg-secondary'}`}>
                      {row.status}
                    </span>
                  </td>
                  <td>{row.contract_duration_days ?? '-'}</td>
                  <td>{row.purchase_price_paid ?? '-'}</td>
                  <td>
                    {row.consumed_amount ?? 0}
                    {row.max_amount ? ` / ${row.max_amount}` : ''}
                  </td>
                  <td>
                    {row.trips_consumed ?? 0}
                    {row.max_trips ? ` / ${row.max_trips}` : ''}
                  </td>
                  <td>{row.contract_start_date ? String(row.contract_start_date).split('T')[0] : '-'}</td>
                  <td>{row.contract_end_date ? String(row.contract_end_date).split('T')[0] : '-'}</td>
                  <td>{row.purchased_at ? convertToLocalTime(row.purchased_at) : '-'}</td>
                  <td>{row.activated_at ? convertToLocalTime(row.activated_at) : '-'}</td>
                  <td>{row.cancelled_at ? convertToLocalTime(row.cancelled_at) : '-'}</td>
                  <td>{row.cancellation_reason ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Purchase Modal ───────────────────────────────────────────────── */}
      <Dialog open={openPurchaseDialog} onClose={() => setOpenPurchaseDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle style={{ display: 'flex', alignItems: 'center' }}>
          {intl.formatMessage({ id: 'BuyPass' }) || 'Buy Fare Product'}
          <Tooltip 
            title={
              <div style={{ fontSize: '13px', lineHeight: '1.5', padding: '4px', whiteSpace: 'pre-wrap' }}>
                {intl.formatMessage({ id: 'BuyFareProductLogicHelp' }) || 
                 'These Fare Products are available for your Passenger Group.\n\n' +
                 '• Assigned Routes: Shows all routes you can ride on, combined from multiple assignments.\n' +
                 '• Assignment Validity: Shows the earliest start date and latest end date among your active assignments.\n\n' +
                 'Note: When using your card on the bus, the system will strictly enforce the exact valid dates for each specific route.'}
              </div>
            } 
            placement="right"
          >
            <IconButton size="small" style={{ marginLeft: '8px' }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '16px' }}>
          {loadingProducts ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <CircularProgress size={24} />
            </div>
          ) : (
            <>
              <FormControl variant="outlined" size="small" fullWidth required>
                <InputLabel>
                  {intl.formatMessage({ id: 'SelectPass' }) || 'Select Fare Product'}
                </InputLabel>
                <Select
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                  label={intl.formatMessage({ id: 'SelectPass' }) || 'Select Fare Product'}
                >
                  {eligibleProducts.map((p) => (
                    <MenuItem key={p.discount_bands_id} value={p.discount_bands_id} disabled={!p.is_eligible}>
                      {currentLanguageCode === 'ar-eg'
                        ? p.pass_name || p.pass_en_name
                        : p.pass_en_name || p.pass_name}{' '}
                      — {p.purchase_price} {intl.formatMessage({ id: 'currency' }) || 'EGP'}
                      {!p.is_eligible && (
                        <span style={{ marginLeft: '8px', color: '#d32f2f', fontSize: '0.85em' }}>
                          ({intl.formatMessage({ id: p.ineligibility_reason }) || p.ineligibility_reason})
                        </span>
                      )}
                    </MenuItem>
                  ))}
                  {eligibleProducts.length === 0 && (
                    <MenuItem disabled value="">
                      {intl.formatMessage({ id: 'NoEligiblePasses' }) || 'No eligible fare products available'}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              {selectedProductDetails && (
                <>
                  <Box mt={2} p={2} border={1} borderColor="grey.300" borderRadius={8} bgcolor="grey.50">
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      {intl.formatMessage({ id: 'BandDetails' }) || 'Fare Product Details'}:
                    </Typography>
                    {[
                      { label: intl.formatMessage({ id: 'PurchasePrice' }) || 'Purchase Price', value: `${selectedProductDetails.purchase_price} ${intl.formatMessage({ id: 'currency' }) || 'EGP'}`, isBold: true },
                      { label: intl.formatMessage({ id: 'ContractDurationDays' }) || 'Contract Duration (Days)', value: selectedProductDetails.contract_duration_days || '-' },
                      { label: intl.formatMessage({ id: 'MaxAmount' }) || 'Max Spend Amount', value: selectedProductDetails.max_amount || '-' },
                      { label: intl.formatMessage({ id: 'MaxTrips' }) || 'Max Trips', value: selectedProductDetails.max_trips || '-' },
                      { label: intl.formatMessage({ id: 'ActivationTrigger' }) || 'Activation Trigger',
                        value: selectedProductDetails.activation_trigger === 'FIRST_USE'
                          ? (intl.formatMessage({ id: 'FirstUse' }) || 'First Use')
                          : selectedProductDetails.activation_trigger === 'SPECIFIC_DATE'
                            ? (intl.formatMessage({ id: 'SpecificDate' }) || 'Specific Date')
                            : (selectedProductDetails.activation_trigger || '-') },
                      { label: intl.formatMessage({ id: 'ValidFrom' }) || 'Product Valid From', value: selectedProductDetails.valid_date_from ? selectedProductDetails.valid_date_from.split('T')[0] : '-' },
                      { label: intl.formatMessage({ id: 'ValidTo' }) || 'Product Valid To', value: selectedProductDetails.valid_date_to ? selectedProductDetails.valid_date_to.split('T')[0] : (intl.formatMessage({ id: 'NoExpiry' }) || 'No Expiry') },
                      { label: intl.formatMessage({ id: 'AssignmentValidFrom' }) || 'Assignment Valid From', value: selectedProductDetails.assignment_valid_from ? selectedProductDetails.assignment_valid_from.split('T')[0] : '-' },
                      { label: intl.formatMessage({ id: 'AssignmentValidTo' }) || 'Assignment Valid To', value: selectedProductDetails.assignment_valid_to ? selectedProductDetails.assignment_valid_to.split('T')[0] : (intl.formatMessage({ id: 'NoExpiry' }) || 'No Expiry') },
                      { label: intl.formatMessage({ id: 'TimeFrom' }) || 'Time From', value: selectedProductDetails.valid_time_from || (intl.formatMessage({ id: 'AllDay' }) || 'All Day') },
                      { label: intl.formatMessage({ id: 'TimeTo' }) || 'Time To', value: selectedProductDetails.valid_time_to || (intl.formatMessage({ id: 'AllDay' }) || 'All Day') },
                      { label: intl.formatMessage({ id: 'AvailableDays' }) || 'Available Days', value: [
                          selectedProductDetails.sunday ? (intl.formatMessage({ id: 'sunday' }) || 'Sun') : null,
                          selectedProductDetails.monday ? (intl.formatMessage({ id: 'monday' }) || 'Mon') : null,
                          selectedProductDetails.tuesday ? (intl.formatMessage({ id: 'tuesday' }) || 'Tue') : null,
                          selectedProductDetails.wednesday ? (intl.formatMessage({ id: 'wednesday' }) || 'Wed') : null,
                          selectedProductDetails.thursday ? (intl.formatMessage({ id: 'thursday' }) || 'Thu') : null,
                          selectedProductDetails.friday ? (intl.formatMessage({ id: 'friday' }) || 'Fri') : null,
                          selectedProductDetails.saturday ? (intl.formatMessage({ id: 'saturday' }) || 'Sat') : null,
                        ].filter(Boolean).length === 7 ? (intl.formatMessage({ id: 'AllDays' }) || 'All Days') : [
                          selectedProductDetails.sunday ? (intl.formatMessage({ id: 'sunday' }) || 'Sun') : null,
                          selectedProductDetails.monday ? (intl.formatMessage({ id: 'monday' }) || 'Mon') : null,
                          selectedProductDetails.tuesday ? (intl.formatMessage({ id: 'tuesday' }) || 'Tue') : null,
                          selectedProductDetails.wednesday ? (intl.formatMessage({ id: 'wednesday' }) || 'Wed') : null,
                          selectedProductDetails.thursday ? (intl.formatMessage({ id: 'thursday' }) || 'Thu') : null,
                          selectedProductDetails.friday ? (intl.formatMessage({ id: 'friday' }) || 'Fri') : null,
                          selectedProductDetails.saturday ? (intl.formatMessage({ id: 'saturday' }) || 'Sat') : null,
                        ].filter(Boolean).join(', ') },
                      { label: intl.formatMessage({ id: 'AssignedRoutes' }) || 'Assigned Routes', value: (selectedProductDetails.assigned_routes && selectedProductDetails.assigned_routes.length > 0) ? selectedProductDetails.assigned_routes.map(r => currentLanguageCode === 'ar-eg' ? r.ar_name || r.en_name : r.en_name || r.ar_name).join(', ') : (intl.formatMessage({ id: 'ApplyOnAllRoutes' }) || 'All Routes') },
                    ].map(({ label, value, isError, isBold }) => (
                      <Typography key={label} variant="body2" color="textSecondary"
                        style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontWeight: isBold ? 'bold' : 'normal', color: isBold ? '#000' : 'inherit' }}>{label}:</span>
                        <span style={{ fontWeight: 'bold', color: isError ? '#d32f2f' : (isBold ? '#000' : 'inherit'), fontSize: isBold ? '1.1em' : 'inherit' }}>{value}</span>
                      </Typography>
                    ))}

                    {/* SPECIFIC_DATE: show date picker */}
                    {selectedProductDetails.activation_trigger === 'SPECIFIC_DATE' && (
                      <Box mt={2}>
                        <Typography variant="body2" style={{ marginBottom: '4px', fontWeight: 500 }}>
                          {intl.formatMessage({ id: 'ValidFrom' }) || 'Contract Start Date'}
                          <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>
                        </Typography>
                        <input
                          type="date"
                          value={contractStartDate}
                          min={new Date().toISOString().split('T')[0]}
                          onChange={(e) => setContractStartDate(e.target.value)}
                          style={{
                            width: '100%', padding: '8px 10px', borderRadius: '4px',
                            border: `1px solid ${contractStartDate ? '#1976d2' : '#d32f2f'}`,
                            fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                          }}
                        />
                        {!contractStartDate && (
                          <Typography variant="caption" style={{ color: '#d32f2f' }}>
                            {intl.formatMessage({ id: 'requiredValidation' }) || 'This field is required'}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                  <Box mt={1} p={2} border={1} borderColor={isInsufficientBalance ? '#d32f2f' : '#4caf50'} borderRadius={8} bgcolor={isInsufficientBalance ? '#ffebee' : '#e8f5e9'}>
                    <Typography variant="body2" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 'bold', color: isInsufficientBalance ? '#d32f2f' : '#2e7d32' }}>
                        {intl.formatMessage({ id: 'YourCurrentBalance' }) || 'Your Current Balance'}:
                      </span>
                      <span style={{ fontWeight: 'bold', color: isInsufficientBalance ? '#d32f2f' : '#2e7d32', fontSize: '1.1em' }}>
                        {fareMediaBalance ?? 0} {intl.formatMessage({ id: 'currency' }) || 'EGP'}
                      </span>
                    </Typography>
                    <Typography variant="body2" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 'bold', color: isInsufficientBalance ? '#d32f2f' : '#2e7d32' }}>
                        {intl.formatMessage({ id: 'BalanceAfterPurchase' }) || 'Balance After Purchase'}:
                      </span>
                      <span style={{ fontWeight: 'bold', color: isInsufficientBalance ? '#d32f2f' : '#2e7d32', fontSize: '1.1em' }}>
                        {((fareMediaBalance || 0) - selectedProductDetails.purchase_price).toFixed(2)} {intl.formatMessage({ id: 'currency' }) || 'EGP'}
                      </span>
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPurchaseDialog(false)} disabled={saving}>
            {intl.formatMessage({ id: 'cancel' }) || 'Cancel'}
          </Button>
          <Button
            onClick={handlePurchase}
            disabled={saving || !selectedProductId ||
              (selectedProductDetails?.activation_trigger === 'SPECIFIC_DATE' && !contractStartDate) || isInsufficientBalance}
            variant="contained"
            style={{ 
              backgroundColor: (saving || !selectedProductId || (selectedProductDetails?.activation_trigger === 'SPECIFIC_DATE' && !contractStartDate) || isInsufficientBalance) ? '#9e9e9e' : '#363740', 
              color: '#fff' 
            }}
          >
            {saving ? <CircularProgress size={18} style={{ color: '#fff' }} /> : (intl.formatMessage({ id: 'Buy' }) || 'Buy')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Cancel Confirmation Modal ────────────────────────────────────── */}
      <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle style={{ color: '#d32f2f' }}>
          {intl.formatMessage({ id: 'CancelFareProduct' }) || 'Cancel Fare Product Instance'}
        </DialogTitle>
        <DialogContent style={{ paddingTop: '16px' }}>
          <Typography variant="body2" style={{ marginBottom: '12px' }}>
            {intl.formatMessage({ id: 'CancelFareProductConfirm' }) || 'Are you sure you want to cancel this fare product instance? This action cannot be undone.'}
          </Typography>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel shrink>
              {intl.formatMessage({ id: 'CancellationReason' }) || 'Cancellation Reason (optional)'}
            </InputLabel>
            <textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              style={{
                width: '100%', padding: '8px', borderRadius: '4px',
                border: '1px solid #ccc', fontFamily: 'inherit', fontSize: '14px',
                resize: 'vertical', marginTop: '4px',
              }}
              placeholder={intl.formatMessage({ id: 'CancellationReason' }) || 'Enter reason...'}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)} disabled={cancelling}>
            {intl.formatMessage({ id: 'Back' }) || 'Back'}
          </Button>
          <Button
            onClick={handleCancel}
            disabled={cancelling}
            variant="contained"
            style={{ backgroundColor: '#d32f2f', color: '#fff' }}
          >
            {cancelling
              ? <CircularProgress size={18} style={{ color: '#fff' }} />
              : (intl.formatMessage({ id: 'confirm' }) || 'Confirm Cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
